import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiX, FiSend, FiLoader } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

// Lightweight Markdown-ish renderer
const MarkdownText = ({ text }) => {
  if (!text) return null;
  
  // Replace ## Header
  let formatted = text.replace(/^## (.*$)/gm, '<h3 class="text-xs font-black uppercase tracking-widest mt-4 mb-2">$1</h3>');
  // Replace **Bold**
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong class="font-black text-chafadi-primary">$1</strong>');
  // Replace *Italic*
  formatted = formatted.replace(/\*(.*?)\*/g, '<em class="italic opacity-80">$1</em>');
  // Replace Newlines
  formatted = formatted.replace(/\n/g, '<br />');

  return <div className="text-xs leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted }} />;
};

const FloatingAssistant = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [showChat, setShowChat] = useState(false);
  const isHomePage = location.pathname === "/";
  const isChatRoute = location.pathname.startsWith("/chat");
  const [messages, setMessages] = useState([
    { 
      role: "assistant", 
      content: t("ai.welcome")
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const apiKey = process.env.REACT_APP_GROQ_API_KEY;
      if (!apiKey || apiKey === "YOUR_GROQ_API_KEY_HERE") {
        throw new Error("Missing API Key");
      }

      const response = await fetch(
        "https://api.groq.com/openai/v1/chat/completions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            model: "llama-3.3-70b-versatile",
            temperature: 0.5,
            max_tokens: 400,
            messages: [
              {
                role: "system",
                content: `You are Chafadi AI assistant.

Chafadi context:
- Chafadi is an e-commerce marketplace focused on Niger.
- Chafadi supports buyer/seller flows, verified sellers, chat, orders, COD, and meetup delivery.

Response rules:
- Keep responses short.
- Default length: 2-4 lines.
- Use Markdown emphasis when helpful: **bold**, *italic*, <u>underline</u>.
- Avoid long answers unless asked.`,
              },
              {
                role: "user",
                content: userMessage,
              },
            ],
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Gemini API Error details:", errorData);
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      const aiResponse = data.choices?.[0]?.message?.content || "Protocol sync timeout. Please try again.";
      
      setMessages(prev => [...prev, { role: "assistant", content: aiResponse }]);
    } catch (error) {
      console.error("Groq Error:", error);
      const errorMsg = error.message === "Missing API Key" 
        ? "AI Hub not configured. Please add your Groq API key to the .env file."
        : "Operational sync error. Please check your network or API configuration.";
      setMessages(prev => [...prev, { role: "assistant", content: errorMsg }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    isChatRoute ? null :
    <div className={`fixed bottom-6 md:bottom-8 right-5 md:right-8 z-[9000] flex flex-col items-end gap-3 transition-opacity duration-300 ${isHomePage ? "hidden lg:flex" : "flex"}`}>
      {/* WhatsApp Button */}
      <a 
        href="https://wa.me/905058186932" 
        target="_blank" 
        rel="noopener noreferrer" 
        className="w-14 h-14 bg-[#25D366] text-white rounded-full flex items-center justify-center text-3xl shadow-[0_10px_30px_rgba(37,211,102,0.4)] hover:scale-110 active:scale-95 transition-all animate-bounce-short"
      >
        <FaWhatsapp />
      </a>

      {/* AI Button */}
      <button 
        className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-[0_10px_40px_rgba(0,0,0,0.15)] hover:scale-110 active:scale-95 transition-all border border-component-border overflow-hidden group relative" 
        onClick={() => setShowChat(true)}
      >
        <img src="/ai.png" alt="AI" className="w-full h-full object-cover group-hover:rotate-12 transition-transform" />
      </button>

      {/* Chat Modal */}
      {showChat && (
        <div className="fixed inset-0 md:inset-auto md:bottom-32 md:right-8 w-full md:max-w-[400px] h-[100dvh] md:h-[600px] z-[10000] animate-zoom-in">
          <div className="bg-white md:rounded-[40px] shadow-2xl border border-component-border overflow-hidden flex flex-col h-full">
            {/* Header */}
            <header className="bg-theme-text p-5 md:p-6 flex items-center justify-between shrink-0">

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-xl p-2 backdrop-blur-md">
                  <img src="/ai.png" alt="" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h3 className="text-white font-black text-xs uppercase tracking-widest leading-none">{t("ai.title")}</h3>
                  <span className="text-emerald-400 text-[8px] font-black uppercase tracking-widest flex items-center gap-1 mt-1">
                    <span className="w-1 h-1 bg-emerald-400 rounded-full animate-ping" />
                    {t("ai.status")}
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setShowChat(false)}
                className="w-10 h-10 rounded-full bg-white/5 text-white/40 flex items-center justify-center hover:bg-white/10 transition-all"
              >
                <FiX />
              </button>
            </header>

            {/* Body */}
            <div className="flex-1 p-6 overflow-y-auto custom-scrollbar bg-theme-bg" ref={scrollRef}>
              <div className="space-y-6">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                    {msg.role === "assistant" && (
                      <div className="w-8 h-8 rounded-lg bg-white border border-component-border p-1 shrink-0">
                        <img src="/ai.png" alt="" className="w-full h-full object-contain" />
                      </div>
                    )}
                    <div className={`max-w-[80%] p-4 shadow-sm border ${
                      msg.role === "user" 
                        ? "bg-chafadi-primary text-white border-transparent rounded-3xl rounded-tr-lg" 
                        : "bg-white text-theme-text border-component-border rounded-3xl rounded-tl-lg"
                    }`}>
                      <MarkdownText text={msg.content} />
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white border border-component-border flex items-center justify-center shrink-0">
                      <FiLoader className="text-chafadi-primary animate-spin" />
                    </div>
                    <div className="bg-white/50 p-4 rounded-3xl rounded-tl-lg border border-dashed border-component-border">
                       <div className="flex gap-1">
                          <div className="w-1 h-1 bg-chafadi-primary rounded-full animate-bounce [animation-delay:-0.3s]" />
                          <div className="w-1 h-1 bg-chafadi-primary rounded-full animate-bounce [animation-delay:-0.15s]" />
                          <div className="w-1 h-1 bg-chafadi-primary rounded-full animate-bounce" />
                       </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Input Overlay */}
            <footer className="p-4 bg-white border-t border-component-border shrink-0">
              <form 
                onSubmit={(e) => { e.preventDefault(); handleSend(); }}
                className="relative"
              >
                <input 
                  type="text" 
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t("ai.placeholder")} 
                  className="w-full h-12 bg-theme-bg border border-component-border rounded-full px-6 text-xs font-bold focus:ring-2 focus:ring-chafadi-primary outline-none pr-12"
                />
                <button 
                  type="submit"
                  disabled={isLoading}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-chafadi-primary text-white rounded-full flex items-center justify-center shadow-lg shadow-orange-100 disabled:opacity-50"
                >
                  <FiSend size={14} />
                </button>
              </form>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingAssistant;
